import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
    ArrowLeft, Download, Send, Wallet, Trash2, Loader2, Undo2, Zap,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import {
    useInvoice, useDownloadInvoiceDocx, useVoidInvoice,
    useRecordInvoicePayment, useMarkInvoiceDelivered, useRefundPortalPayment,
    type InvoiceLine,
} from '@/api/invoices';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';
import { FormField, Input } from '@/components/ui/FormField';

/*
 * Invoice detail — header card, line table, action rail. Each action is its
 * own dialog so the page doesn't balloon a dozen inline forms.
 *
 * Void / payment / delivery all mutate server state that changes claim
 * status too; the underlying mutation hooks invalidate the ['claims'] query
 * so the Claims tab stays in sync without a manual refresh.
 */

function fmtMoney(n: number): string {
    return (n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtDate(iso: string | null): string {
    if (!iso) return '';
    return String(iso).slice(0, 10);
}

function todayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function InvoiceDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const canEdit = useAuthStore((s) => s.hasPermission('edit_billing'));
    // Phase 6 of pma-stripe-payments — process_payments is the same
    // perm the server's refund route gates on (seeded into Admin +
    // Billing roles in Phase 1).
    const canRefund = useAuthStore((s) => s.hasPermission('process_payments'));
    const { data, isLoading, isError } = useInvoice(id);

    const downloadM = useDownloadInvoiceDocx();
    const [payOpen, setPayOpen] = useState(false);
    const [deliverOpen, setDeliverOpen] = useState(false);
    const [voidOpen, setVoidOpen] = useState(false);
    const [refundOpen, setRefundOpen] = useState(false);

    if (isLoading) {
        return (
            <div className="p-6 max-w-[1400px] mx-auto">
                <Card className="p-10 flex items-center justify-center text-text-muted">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading invoice…
                </Card>
            </div>
        );
    }
    if (isError || !data) {
        return (
            <div className="p-6 max-w-[1400px] mx-auto">
                <Alert variant="danger">Failed to load invoice.</Alert>
            </div>
        );
    }

    const isVoided = data.status === 'Voided';
    const isPaid   = data.status === 'Paid';

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <Link to="/billing/invoices" className="text-xs text-text-muted hover:text-text inline-flex items-center gap-1 mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Invoices
            </Link>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-text inline-flex items-center gap-3">
                        {data.displayId}
                        <StatusBadge status={data.status} />
                    </h1>
                    <p className="text-xs text-text-subtle mt-1">{data.clientName}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={downloadM.isPending}
                        onClick={() => downloadM.mutate({ id: data.id, displayId: data.displayId })}
                    >
                        <Download className="w-3.5 h-3.5" />
                        {downloadM.isPending ? 'Downloading…' : 'Download .docx'}
                    </Button>
                    {canEdit && !isVoided && (
                        <>
                            <Button variant="ghost" size="sm" onClick={() => setDeliverOpen(true)}>
                                <Send className="w-3.5 h-3.5" /> Mark delivered
                            </Button>
                            {!isPaid && (
                                <Button variant="primary" size="sm" onClick={() => setPayOpen(true)}>
                                    <Wallet className="w-3.5 h-3.5" /> Record payment
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => setVoidOpen(true)} className="text-error-text">
                                <Trash2 className="w-3.5 h-3.5" /> Void
                            </Button>
                        </>
                    )}
                    {/*
                      Refund affordance is independent of edit_billing /
                      voided / paid because: (a) refunds need their own
                      perm (process_payments), and (b) a refund makes
                      sense on a Paid invoice — that's the common case.
                      Hidden when there's no portal payment to refund.
                    */}
                    {canRefund && data.portalPaymentRefundable > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setRefundOpen(true)}>
                            <Undo2 className="w-3.5 h-3.5" /> Refund
                        </Button>
                    )}
                </div>
            </div>

            {isVoided && (
                <Alert variant="warning" className="mb-4">
                    This invoice is voided{data.voidedAt ? ` (on ${fmtDate(data.voidedAt)})` : ''}.
                    Unpaid claims were released back to Ready; partially-paid claims stay frozen for audit.
                </Alert>
            )}

            {/*
              Phase 8 of pma-stripe-payments — read-only autopay status.
              Staff don't toggle this (parent owns the decision), but
              the biller needs to see the schedule so they don't reach
              out about an invoice that's about to charge itself. Hidden
              when autopay is off OR the invoice is already settled.
            */}
            {data.autopayEnabled && !isVoided && !isPaid && (
                <Alert variant="info" className="mb-4">
                    <span className="inline-flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5" />
                        <span>
                            Autopay is on for this invoice
                            {data.dueDate ? ` — scheduled for ${fmtDate(data.dueDate)}` : ''}.
                            The parent set this; do not record a manual payment.
                        </span>
                    </span>
                </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <SummaryCard label="Issued"   value={fmtDate(data.issuedAt)} />
                <SummaryCard label="Due"      value={fmtDate(data.dueDate)} />
                <SummaryCard label="Delivery" value={data.deliveryMethod ? `${data.deliveryMethod} · ${fmtDate(data.deliveredAt)}` : 'Not yet delivered'} />
                <SummaryCard label="Subtotal" value={fmtMoney(data.subtotal)} />
                <SummaryCard label="Paid"     value={fmtMoney(data.paidAmount)} />
                <SummaryCard label="Balance"  value={fmtMoney(data.balance)} highlight={data.balance > 0} />
                {data.refundedInPortal > 0 && (
                    <SummaryCard label="Refunded" value={fmtMoney(data.refundedInPortal)} />
                )}
            </div>

            <Card className="p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-border">
                    <h2 className="font-medium text-sm text-text">Line items</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-surface-muted text-text-subtle uppercase tracking-wider text-2xs">
                            <tr>
                                <th className="px-3 py-2 text-left font-bold">Claim #</th>
                                <th className="px-3 py-2 text-left font-bold">DOS</th>
                                <th className="px-3 py-2 text-left font-bold">Code</th>
                                <th className="px-3 py-2 text-right font-bold">Units</th>
                                <th className="px-3 py-2 text-right font-bold">Rate</th>
                                <th className="px-3 py-2 text-right font-bold">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.lines.map((l) => (
                                <LineRow key={l.id} line={l} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {data.notes && (
                <Card className="mt-4 p-4">
                    <h2 className="font-medium text-sm text-text mb-2">Notes</h2>
                    <p className="text-xs text-text-muted whitespace-pre-wrap">{data.notes}</p>
                </Card>
            )}

            <RecordInvoicePaymentDialog
                open={payOpen}
                onOpenChange={setPayOpen}
                invoiceId={data.id}
                displayId={data.displayId}
                balance={data.balance}
            />
            <MarkDeliveredDialog
                open={deliverOpen}
                onOpenChange={setDeliverOpen}
                invoiceId={data.id}
                displayId={data.displayId}
                currentMethod={data.deliveryMethod}
            />
            <VoidInvoiceDialog
                open={voidOpen}
                onOpenChange={setVoidOpen}
                invoiceId={data.id}
                displayId={data.displayId}
                onSuccess={() => { setVoidOpen(false); navigate('/billing/invoices'); }}
            />
            <RefundPortalPaymentDialog
                open={refundOpen}
                onOpenChange={setRefundOpen}
                invoiceId={data.id}
                displayId={data.displayId}
                refundableDollars={data.portalPaymentRefundable}
            />
        </div>
    );
}

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <Card className="p-3">
            <div className="text-2xs uppercase tracking-wider text-text-subtle">{label}</div>
            <div className={`text-base tabular-nums mt-1 ${highlight ? 'text-text font-semibold' : 'text-text'}`}>
                {value || '—'}
            </div>
        </Card>
    );
}

function LineRow({ line }: { line: InvoiceLine }) {
    return (
        <tr className="border-t border-border">
            <td className="px-3 py-2 font-mono text-text">
                <Link to={`/billing/${line.claimId}`} className="hover:text-accent-text">{line.claimDisplayId}</Link>
                {line.prSourceDisplayId && (
                    <span className="ml-2 text-2xs text-text-subtle" title={`Patient responsibility from ${line.prSourceDisplayId}`}>
                        PR from {line.prSourceDisplayId}
                    </span>
                )}
            </td>
            <td className="px-3 py-2 text-text-muted tabular-nums">{fmtDate(line.dos)}</td>
            <td className="px-3 py-2 text-text-muted">{line.serviceCode}</td>
            <td className="px-3 py-2 text-right tabular-nums">{line.units}</td>
            <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(line.unitRate)}</td>
            <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(line.amountSnapshot)}</td>
        </tr>
    );
}

// ── Dialogs ──────────────────────────────────────────────────────

function RecordInvoicePaymentDialog({
    open, onOpenChange, invoiceId, displayId, balance,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    invoiceId: string;
    displayId: string;
    balance: number;
}) {
    const m = useRecordInvoicePayment();
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(todayIso());
    const [ref, setRef] = useState('');

    function reset() {
        setAmount('');
        setDate(todayIso());
        setRef('');
        m.reset();
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        const paidAmount = Number(amount);
        if (!Number.isFinite(paidAmount) || paidAmount <= 0) return;
        try {
            await m.mutateAsync({
                id: invoiceId,
                payload: { paidAmount, paymentDate: date, payerRef: ref.trim() || undefined },
            });
            onOpenChange(false);
            reset();
        } catch { /* surfaced below */ }
    }

    const err = m.error instanceof AxiosError
        ? (m.error.response?.data as { error?: string })?.error
        : null;

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),28rem)]">
                <DialogHeader>
                    <DialogTitle>Record payment — {displayId}</DialogTitle>
                    <DialogDescription>
                        Payment is prorated across invoice lines. Outstanding balance: {fmtMoney(balance)}.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} noValidate>
                    {err && <Alert variant="danger" className="mb-4">{err}</Alert>}
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Amount" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Payment date" required>
                            {(id) => (
                                <Input id={id} type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
                            )}
                        </FormField>
                    </div>
                    <FormField label="Reference #" hint="Check #, card last 4, etc.">
                        {(id) => (
                            <Input id={id} value={ref} maxLength={128} onChange={(e) => setRef(e.target.value)} />
                        )}
                    </FormField>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={m.isPending}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={m.isPending || !amount || !Number.isFinite(Number(amount)) || Number(amount) <= 0}>
                            {m.isPending ? 'Saving…' : 'Record payment'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function MarkDeliveredDialog({
    open, onOpenChange, invoiceId, displayId, currentMethod,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    invoiceId: string;
    displayId: string;
    currentMethod: 'portal' | 'mail' | 'manual' | null;
}) {
    const m = useMarkInvoiceDelivered();
    const [method, setMethod] = useState<'portal' | 'mail' | 'manual'>(currentMethod ?? 'mail');
    const [date, setDate] = useState(todayIso());
    useEffect(() => { if (open) setMethod(currentMethod ?? 'mail'); }, [open, currentMethod]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            await m.mutateAsync({
                id: invoiceId,
                payload: { method, deliveredAt: `${date}T12:00:00Z` },
            });
            onOpenChange(false);
            m.reset();
        } catch { /* surfaced below */ }
    }

    const err = m.error instanceof AxiosError
        ? (m.error.response?.data as { error?: string })?.error
        : null;

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) m.reset(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),26rem)]">
                <DialogHeader>
                    <DialogTitle>Mark delivered — {displayId}</DialogTitle>
                    <DialogDescription>
                        Records how and when the invoice reached the client. The parent portal auto-sets this on first view when delivery is still blank.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} noValidate>
                    {err && <Alert variant="danger" className="mb-4">{err}</Alert>}
                    <FormField label="Method" required>
                        {(id) => (
                            <select
                                id={id}
                                value={method}
                                onChange={(e) => setMethod(e.target.value as 'portal' | 'mail' | 'manual')}
                                className="w-full h-9 px-2 text-sm bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="mail">Mail</option>
                                <option value="portal">Portal</option>
                                <option value="manual">Manual (hand-off, email, etc.)</option>
                            </select>
                        )}
                    </FormField>
                    <FormField label="Delivery date" required>
                        {(id) => (
                            <Input id={id} type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
                        )}
                    </FormField>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={m.isPending}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={m.isPending}>
                            {m.isPending ? 'Saving…' : 'Mark delivered'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// Phase 6 of pma-stripe-payments — staff trigger for refunding a
// portal payment. The server returns 202 + queued; the actual
// invoices.refunded_in_portal_cents flip lands a few seconds later
// when Stripe's charge.refunded webhook arrives. Mutation success
// invalidates the invoice query so the next refetch picks up the
// new refunded total automatically.
function RefundPortalPaymentDialog({
    open, onOpenChange, invoiceId, displayId, refundableDollars,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    invoiceId: string;
    displayId: string;
    refundableDollars: number;
}) {
    const m = useRefundPortalPayment();
    // Default the input to the full refundable amount — the common
    // case is "refund this whole payment" and pre-filling saves a
    // typing step. Operator can override down to a smaller partial.
    const [amount, setAmount] = useState(refundableDollars.toFixed(2));
    const [reason, setReason] = useState('');
    const [confirmed, setConfirmed] = useState(false);

    useEffect(() => {
        if (open) {
            setAmount(refundableDollars.toFixed(2));
            setReason('');
            setConfirmed(false);
            m.reset();
        }
        // m intentionally omitted — useMutation's hook identity is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, refundableDollars]);

    const dollars = Number(amount);
    const cents = Math.round(dollars * 100);
    // Client-side guard mirrors the server's over-refund 400 — gives
    // the operator inline feedback without a round-trip. Server stays
    // authoritative.
    const refundableCents = Math.round(refundableDollars * 100);
    const valid =
        Number.isFinite(dollars) &&
        cents > 0 &&
        cents <= refundableCents;

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!valid || !confirmed) return;
        try {
            await m.mutateAsync({
                id: invoiceId,
                payload: { amountCents: cents, reason: reason.trim() || undefined },
            });
            onOpenChange(false);
        } catch { /* surfaced below */ }
    }

    const err = m.error instanceof AxiosError
        ? (m.error.response?.data as { error?: string })?.error
        : null;

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) m.reset(); }}>
            <DialogContent className="w-[min(calc(100vw-2rem),28rem)]">
                <DialogHeader>
                    <DialogTitle>Refund — {displayId}</DialogTitle>
                    <DialogDescription>
                        Refunds the parent's card via Stripe. Up to {fmtMoney(refundableDollars)} can be refunded on this payment. The refund typically appears on the parent's statement within 5–10 business days.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} noValidate>
                    {err && <Alert variant="danger" className="mb-4">{err}</Alert>}
                    <FormField label="Refund amount" required>
                        {(id) => (
                            <Input
                                id={id}
                                type="number"
                                min={0.01}
                                max={refundableDollars}
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                aria-invalid={!valid}
                            />
                        )}
                    </FormField>
                    {!valid && amount !== '' && (
                        <p className="-mt-3 mb-3 text-xs text-error-text" role="alert">
                            {cents <= 0
                                ? 'Enter an amount greater than $0.'
                                : `Cannot exceed the refundable balance of ${fmtMoney(refundableDollars)}.`}
                        </p>
                    )}
                    <FormField label="Reason" hint="Optional — appears in the audit log and on the Stripe refund.">
                        {(id) => (
                            <Input
                                id={id}
                                maxLength={255}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        )}
                    </FormField>
                    {/*
                      Refunds are real money leaving the practice's bank
                      account; an extra checkbox guard makes the action
                      deliberate without adding a second confirmation
                      dialog. Mirrors the same pattern as the void
                      flow's reason-then-Void.
                    */}
                    <label className="flex items-start gap-2 text-xs text-text-muted mb-4 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span>
                            I understand this will refund {fmtMoney(Number.isFinite(dollars) ? dollars : 0)} to the parent's card.
                        </span>
                    </label>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={m.isPending}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={m.isPending || !valid || !confirmed}>
                            {m.isPending ? 'Queuing…' : 'Refund'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function VoidInvoiceDialog({
    open, onOpenChange, invoiceId, displayId, onSuccess,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    invoiceId: string;
    displayId: string;
    onSuccess: () => void;
}) {
    const m = useVoidInvoice();
    const [reason, setReason] = useState('');

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            await m.mutateAsync({ id: invoiceId, payload: { reason: reason.trim() || undefined } });
            setReason('');
            onSuccess();
        } catch { /* surfaced below */ }
    }

    const err = m.error instanceof AxiosError
        ? (m.error.response?.data as { error?: string })?.error
        : null;

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setReason(''); m.reset(); } }}>
            <DialogContent className="w-[min(calc(100vw-2rem),28rem)]">
                <DialogHeader>
                    <DialogTitle>Void {displayId}?</DialogTitle>
                    <DialogDescription>
                        Unpaid claims on this invoice return to Ready. Partially-paid claims stay on the invoice for the audit paper trail.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} noValidate>
                    {err && <Alert variant="danger" className="mb-4">{err}</Alert>}
                    <FormField label="Reason" hint="Optional — appended to notes and the audit row.">
                        {(id) => (
                            <Input id={id} maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
                        )}
                    </FormField>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={m.isPending}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={m.isPending} className="bg-error text-white hover:bg-error">
                            {m.isPending ? 'Voiding…' : 'Void invoice'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
